import React, { useState } from 'react';
import { Input, Collapse, Space, Typography, Tag, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../../../components/common/Toast';
import { produce } from 'immer';
import {
  SearchOutlined,
  FileTextOutlined,
  AlignLeftOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  NumberOutlined,
  DollarOutlined,
  MailOutlined,
  PhoneOutlined,
  LinkOutlined,
  TableOutlined,
  FileAddOutlined,
  StarOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  UserOutlined,
  TagsOutlined,
  DownOutlined,
  AppstoreOutlined,
  BorderOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  UploadOutlined,
  EnvironmentOutlined,
  CopyOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { selectTemplateData, updateTemplateData } from '../../../../store/slices/rfpTemplateSlice';
import { selectSelectedElement } from '../../../../store/slices/rfpBuilderSlice';

const { Text, Title } = Typography;
const { Panel } = Collapse;

const fieldTypes = [
  {
    category: 'Basic Fields',
    icon: <FileTextOutlined />,
    fields: [
      { type: 'text', label: 'Text Input', icon: <AlignLeftOutlined />, description: 'Single line text' },
      { type: 'textarea', label: 'Text Area', icon: <FileTextOutlined />, description: 'Multi-line text' },
      { type: 'richtext', label: 'Rich Text', icon: <EditOutlined />, description: 'Formatted text editor' },
      { type: 'number', label: 'Number', icon: <NumberOutlined />, description: 'Numeric input' },
      { type: 'email', label: 'Email', icon: <MailOutlined />, description: 'Email address' },
      { type: 'phone', label: 'Phone', icon: <PhoneOutlined />, description: 'Phone number' },
      { type: 'url', label: 'URL', icon: <LinkOutlined />, description: 'Website link' },
    ],
  },
  {
    category: 'Selection Fields',
    icon: <AppstoreOutlined />,
    fields: [
      { type: 'select', label: 'Dropdown', icon: <DownOutlined />, description: 'Single select' },
      { type: 'multiselect', label: 'Multi-Select', icon: <AppstoreOutlined />, description: 'Multiple options' },
      { type: 'radio', label: 'Radio Buttons', icon: <CheckCircleOutlined />, description: 'Single choice' },
      { type: 'checkbox', label: 'Checkboxes', icon: <CheckSquareOutlined />, description: 'Multiple choices' },
    ],
  },
  {
    category: 'Date & Time',
    icon: <CalendarOutlined />,
    fields: [
      { type: 'date', label: 'Date', icon: <CalendarOutlined />, description: 'Date picker' },
      { type: 'datetime', label: 'Date & Time', icon: <FieldTimeOutlined />, description: 'Date and time' },
      { type: 'time', label: 'Time', icon: <ClockCircleOutlined />, description: 'Time picker' },
      { type: 'daterange', label: 'Date Range', icon: <CalendarOutlined />, description: 'Start and end date' },
    ],
  },
  {
    category: 'Advanced Fields',
    icon: <StarOutlined />,
    fields: [
      { type: 'file', label: 'File Upload', icon: <UploadOutlined />, description: 'Upload documents' },
      { type: 'rating', label: 'Rating', icon: <StarOutlined />, description: 'Star rating' },
      { type: 'currency', label: 'Currency', icon: <DollarOutlined />, description: 'Money amount' },
      { type: 'percentage', label: 'Percentage', icon: <PercentageOutlined />, description: 'Percentage value' },
      { type: 'user', label: 'User Selector', icon: <UserOutlined />, description: 'Select user' },
      { type: 'location', label: 'Location', icon: <EnvironmentOutlined />, description: 'Address/location' },
    ],
  },
  {
    category: 'Complex Fields',
    icon: <TableOutlined />,
    fields: [
      { type: 'table', label: 'Table/Grid', icon: <TableOutlined />, description: 'Excel-like grid' },
      { type: 'repeater', label: 'Repeater', icon: <CopyOutlined />, description: 'Repeatable section' },
    ],
  },
];

function DraggableField({ field, onClickAdd }) {
  const { attributes, listeners, setNodeRef, transform, isDragging, setActivatorNodeRef } = useDraggable({
    id: `field-${field.type}`,
    data: { type: 'field', fieldType: field.type, label: field.label },
  });

  const [dragStarted, setDragStarted] = useState(false);

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: 'pointer',
    padding: '6px 10px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    marginBottom: '4px',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const handleClick = (e) => {
    console.log('🟢 Field clicked:', field.label, 'dragStarted:', dragStarted);

    // Prevent click if drag was started
    if (dragStarted) {
      console.log('🚫 Click prevented - drag was started');
      return;
    }

    e.stopPropagation();
    onClickAdd(field);
  };

  return (
    <Tooltip title={field.description} placement="right">
      <div
        ref={setNodeRef}
        style={style}
        onClick={handleClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#F3E8FF';
          e.currentTarget.style.borderColor = '#8B5CF6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#F9FAFB';
          e.currentTarget.style.borderColor = '#E5E7EB';
        }}
      >
        <span
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          onMouseDown={() => {
            console.log('🟢 Drag handle mousedown - starting drag tracking');
            setDragStarted(false);
            // Use a small delay to distinguish between click and drag
            setTimeout(() => setDragStarted(true), 150);
          }}
          onMouseUp={() => {
            console.log('🟢 Drag handle mouseup - resetting drag tracking');
            setTimeout(() => setDragStarted(false), 50);
          }}
          style={{ color: '#8B5CF6', fontSize: '14px', display: 'flex', alignItems: 'center', cursor: 'grab' }}
        >
          {field.icon}
        </span>
        <Text strong style={{ fontSize: '13px' }}>
          {field.label}
        </Text>
      </div>
    </Tooltip>
  );
}

export default function ComponentLibrary() {
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();
  const templateData = useSelector(selectTemplateData);
  const selectedElement = useSelector(selectSelectedElement);

  const handleClickAdd = (field) => {
    console.log('🟢 Click add triggered for:', field.label);
    console.log('🟢 selectedElement:', selectedElement);

    // Parse selected element path to get target indices
    let targetSheetIdx = 0;
    let targetSectionIdx = 0;
    let targetCategoryIdx = 0;

    if (selectedElement && selectedElement.path) {
      console.log('🟢 Found selectedElement.path:', selectedElement.path);
      const match = selectedElement.path.match(/sheet-(\d+)(?:-section-(\d+))?(?:-category-(\d+))?/);
      if (match) {
        targetSheetIdx = parseInt(match[1]);
        targetSectionIdx = match[2] !== undefined ? parseInt(match[2]) : 0;
        targetCategoryIdx = match[3] !== undefined ? parseInt(match[3]) : 0;
        console.log('🟢 Parsed indices from path:', { targetSheetIdx, targetSectionIdx, targetCategoryIdx });
      } else {
        console.log('❌ Path regex did not match');
      }
    } else {
      console.log('❌ No selectedElement or no path, using defaults:', { targetSheetIdx, targetSectionIdx, targetCategoryIdx });
    }

    console.log('Click add target:', { targetSheetIdx, targetSectionIdx, targetCategoryIdx });

    // Add field to template data at the target location
    const newData = produce(templateData, (draft) => {
      if (!draft.sheets || draft.sheets.length === 0) {
        draft.sheets = [{
          id: `sheet_${Date.now()}`,
          name: 'Main',
          sections: [],
        }];
        targetSheetIdx = 0;
      }

      const sheet = draft.sheets[targetSheetIdx];
      if (!sheet) {
        console.error(`Sheet at index ${targetSheetIdx} not found`);
        return;
      }

      if (!sheet.sections || sheet.sections.length === 0) {
        sheet.sections = [{
          id: `section_${Date.now()}`,
          name: 'General',
          categories: [],
        }];
        targetSectionIdx = 0;
      }

      const section = sheet.sections[targetSectionIdx];
      if (!section) {
        console.error(`Section at index ${targetSectionIdx} not found in sheet ${targetSheetIdx}`);
        return;
      }

      if (!section.categories || section.categories.length === 0) {
        section.categories = [{
          id: `category_${Date.now()}`,
          name: 'Questions',
          questions: [],
        }];
        targetCategoryIdx = 0;
      }

      const category = section.categories[targetCategoryIdx];
      if (!category) {
        console.error(`Category at index ${targetCategoryIdx} not found in section ${targetSectionIdx}`);
        return;
      }

      if (!category.questions) {
        category.questions = [];
      }

      category.questions.push({
        id: `question_${Date.now()}`,
        question: `New ${field.label}`,
        field_type: field.type,
        required: false,
        help_text: '',
        options: field.type === 'select' || field.type === 'multiselect' || field.type === 'radio' || field.type === 'checkbox' ? [] : undefined,
      });
    });

    console.log('🟢 Dispatching updateTemplateData with newData');
    dispatch(updateTemplateData(newData));
    console.log('🟢 Dispatch complete, showing toast');
    toast.success(`Added ${field.label} field`);
    console.log('🟢 Click-to-add complete!');
  };

  const filteredTypes = fieldTypes.map((category) => ({
    ...category,
    fields: category.fields.filter(
      (field) =>
        field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.type.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.fields.length > 0);

  return (
    <div style={{ padding: '12px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={5} style={{ marginBottom: '4px', fontSize: '15px' }}>
            Component Library
          </Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Drag or click to add fields
          </Text>
        </div>

        <Input
          placeholder="Search fields..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          size="small"
        />

        <Collapse
          defaultActiveKey={['Basic Fields']}
          ghost
          expandIconPosition="end"
        >
          {filteredTypes.map((category) => (
            <Panel
              key={category.category}
              header={
                <Space>
                  <span style={{ fontSize: '16px' }}>{category.icon}</span>
                  <Text strong>{category.category}</Text>
                  <Tag color="purple">{category.fields.length}</Tag>
                </Space>
              }
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {category.fields.map((field) => (
                  <DraggableField key={field.type} field={field} onClickAdd={handleClickAdd} />
                ))}
              </Space>
            </Panel>
          ))}
        </Collapse>

        {filteredTypes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">No fields match your search</Text>
          </div>
        )}
      </Space>
    </div>
  );
}
